import { useState, ChangeEvent } from 'react'
import { Label, Slider, Textarea, Checkbox, Input, Button, Separator } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { FileText, X } from 'lucide-react'
import type { ItemInput } from '@creanizer/types'

interface ItemFormAdvancedProps {
  input: ItemInput
  onChange: (input: ItemInput) => void
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
  hasWorldContext?: boolean
}

interface UploadedFile {
  id: string
  name: string
  content: string
}

interface UploadedImage {
  id: string
  name: string
  data: string
  size: number
}

export function ItemFormAdvanced({ input, onChange, useStreaming, onStreamingChange, hasWorldContext }: ItemFormAdvancedProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([])

  // テキストファイルアップロード処理（複数対応）
  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const newFiles: UploadedFile[] = []

    for (const file of Array.from(files)) {
      try {
        const text = await file.text()
        newFiles.push({
          id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: file.name,
          content: text
        })
      } catch (error) {
        console.error('ファイル読み込みエラー:', error)
        alert(`${file.name} の読み込みに失敗しました`)
      }
    }

    if (newFiles.length > 0) {
      const allFiles = [...uploadedFiles, ...newFiles]
      setUploadedFiles(allFiles)
      const allFilesContent = allFiles
        .map(file => `【ファイル: ${file.name}】\n${file.content}`)
        .join('\n\n')
      onChange({ ...input, uploadedContent: allFilesContent || undefined })
    }

    e.target.value = ''
  }

  const handleFileRemove = (fileId: string) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId)
    setUploadedFiles(newFiles)
    const allFilesContent = newFiles
      .map(file => `【ファイル: ${file.name}】\n${file.content}`)
      .join('\n\n')
    onChange({ ...input, uploadedContent: allFilesContent || undefined })
  }

  // 画像アップロード処理
  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const maxSize = 20 * 1024 * 1024 // 20MB制限
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

    const newImages: UploadedImage[] = []

    for (const file of Array.from(files)) {
      if (!validTypes.includes(file.type)) {
        alert(`${file.name} はサポートされていない形式です（対応: JPEG/PNG/GIF/WebP）`)
        continue
      }

      if (file.size > maxSize) {
        alert(`${file.name} はファイルサイズが大きすぎます (最大20MB)`)
        continue
      }

      try {
        const reader = new FileReader()
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        newImages.push({
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: file.name,
          data: base64,
          size: file.size
        })
      } catch (error) {
        console.error('画像読み込みエラー:', error)
        alert(`${file.name} の読み込みに失敗しました`)
      }
    }

    if (newImages.length > 0) {
      const allImages = [...uploadedImages, ...newImages].slice(0, 5)
      setUploadedImages(allImages)
      onChange({ ...input, images: allImages.map(img => img.data) })
    }

    e.target.value = ''
  }

  const handleImageRemove = (imageId: string) => {
    const newImages = uploadedImages.filter(img => img.id !== imageId)
    setUploadedImages(newImages)
    onChange({ ...input, images: newImages.length > 0 ? newImages.map(img => img.data) : undefined })
  }

  return (
    <div className="space-y-4">
      {/* 技術レベル - 世界観選択時は非表示 */}
      {!hasWorldContext && (
        <div>
          <Label htmlFor="techLevel">技術レベル</Label>
          <Select
            value={input.techLevel || 'medieval'}
            onValueChange={(value) => onChange({ ...input, techLevel: value as ItemInput['techLevel'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="技術レベルを選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="primitive">原始的</SelectItem>
              <SelectItem value="medieval">中世</SelectItem>
              <SelectItem value="industrial">産業革命</SelectItem>
              <SelectItem value="modern">現代</SelectItem>
              <SelectItem value="futuristic">未来的</SelectItem>
              <SelectItem value="magical">魔法的</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* パワーレベル */}
      <div>
        <Label htmlFor="powerLevel">パワーレベル: {input.powerLevel || 5}/10</Label>
        <Slider
          id="powerLevel"
          min={1}
          max={10}
          step={1}
          value={[input.powerLevel || 5]}
          onValueChange={([value]) => onChange({ ...input, powerLevel: value })}
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>弱い</span>
          <span>普通</span>
          <span>強力</span>
        </div>
      </div>

      {/* 創造性レベル */}
      <div>
        <Label htmlFor="creativityLevel">創造性レベル: {input.creativityLevel || 7}/10</Label>
        <Slider
          id="creativityLevel"
          min={1}
          max={10}
          step={1}
          value={[input.creativityLevel || 7]}
          onValueChange={([value]) => onChange({ ...input, creativityLevel: value })}
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>一般的</span>
          <span>バランス</span>
          <span>独創的</span>
        </div>
      </div>

      {/* 追加設定 */}
      <div>
        <Label htmlFor="additionalSettings">追加設定（オプション）</Label>
        <Textarea
          id="additionalSettings"
          placeholder="特別な要望や設定があれば入力..."
          value={input.additionalSettings || ''}
          onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
          rows={3}
        />
      </div>

      {/* プロモード */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="useProMode"
          checked={input.useProMode || false}
          onCheckedChange={(checked) => onChange({ ...input, useProMode: checked as boolean })}
        />
        <Label htmlFor="useProMode" className="text-sm font-medium leading-none">
          Proモードを使用（より詳細で高品質な生成）
        </Label>
      </div>

      {/* Gemini 3 Flash モード */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="useGemini3FlashMode"
          checked={input.useGemini3FlashMode || false}
          onCheckedChange={(checked) => onChange({ ...input, useGemini3FlashMode: checked as boolean })}
        />
        <Label htmlFor="useGemini3FlashMode" className="text-sm font-medium leading-none">
          Gemini 3 Flash モード（試験版）
        </Label>
      </div>

      {/* Gemini 3 Pro モード */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="useGemini3Mode"
          checked={input.useGemini3Mode || false}
          onCheckedChange={(checked) => onChange({ ...input, useGemini3Mode: checked as boolean })}
        />
        <Label htmlFor="useGemini3Mode" className="text-sm font-medium leading-none">
          Gemini 3 Pro モード（有料APIのみ・試験版）
        </Label>
      </div>

      {/* ストリーミングモード */}
      {onStreamingChange && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="useStreaming"
            checked={useStreaming || false}
            onCheckedChange={(checked) => onStreamingChange(checked as boolean)}
          />
          <Label htmlFor="useStreaming" className="text-sm font-medium leading-none">
            ストリーミングを使用（リアルタイム生成・進捗表示）
          </Label>
        </div>
      )}

      <Separator />

      {/* 参考資料ファイルアップロード */}
      <div className="space-y-2">
        <Label htmlFor="fileUpload">参考資料ファイル（オプション）</Label>
        <Input
          id="fileUpload"
          type="file"
          accept=".txt,.md,.json"
          multiple
          onChange={handleFileUpload}
        />
        <p className="text-xs text-muted-foreground">
          テキストファイル（.txt/.md/.json）を複数アップロードして参考資料として使用できます
        </p>

        {/* アップロード済みファイル一覧 */}
        {uploadedFiles.length > 0 && (
          <div className="space-y-2 mt-2">
            {uploadedFiles.map(file => (
              <div key={file.id} className="flex items-center gap-2 p-2 bg-muted rounded">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm flex-1 truncate">{file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFileRemove(file.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 画像アップロード */}
      <div className="space-y-2">
        <Label htmlFor="imageUpload">参考画像（オプション）</Label>
        <Input
          id="imageUpload"
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          multiple
          onChange={handleImageUpload}
        />
        <p className="text-xs text-muted-foreground">
          アイテムの外見参考として画像をアップロードできます（対応形式: JPEG/PNG/GIF/WebP、最大5枚、各20MBまで）
        </p>

        {/* アップロード済み画像プレビュー */}
        {uploadedImages.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            {uploadedImages.map(img => (
              <div key={img.id} className="relative group">
                <img
                  src={img.data}
                  alt={img.name}
                  className="w-full h-20 object-cover rounded border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleImageRemove(img.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
                <p className="text-xs text-muted-foreground truncate mt-1">{img.name}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
